import { Form, Formik } from "formik";
import React, { useContext } from "react";
import { useMessageSource } from "react-message-source";
import ApplicationPageContent from "../../ui/layout/page/content/application";
import ContentFooter from "../../ui/layout/page/content/footer";
import NavigationBar from "../../ui/layout/page/content/footer/navigation-bar";
import ContentHeader from "../../ui/layout/page/content/header";
import FormContainer from "../../ui/layout/page/forms-container/form-base-container";
import FlowHeader from "../../ui/widgets/flow-header";
import ProductImage from "../shared/components/product-image";
import { BasicAccountBenefitsModel } from "./types";
import { useAnnualFlightSpendingOptions } from "./useAnnualFlightSpendingOptions";
import useAdditionalBenefitsValidationSchema from "./validator";
import { LanguageContext } from "../../context/languageContext";
import { CashOnCard } from "./cashOnCard";
import { PartnerPlusBenefits } from "./partnerPlus";
import { ProductContext } from "../../context/product-context/productContext";
import InvalidFormError, {
  isSubmitErrorMessageShown,
} from "../../ui/layout/page/content/footer/form-error/invalid-form-error";
import { MembershipReward } from "./membershipReward";
import CustomizationCardOptions from "./customization-card-options";
import { CancelApplicationContext } from "../../context/cancelApplicationContext";
import InsurancePlus from "./insurance-plus";
import TurnoverBonus from "./turover-bonus";

export type AdditionalBenefitsProps = {
  initial: BasicAccountBenefitsModel;
  context: {
    companyName: string;
  };
  next: (model: BasicAccountBenefitsModel) => Promise<void>;
  previous: () => void;
};

export default function AdditionalBenefits({
  initial,
  context,
  next,
  previous,
}: AdditionalBenefitsProps): React.JSX.Element {
  const { getMessage } = useMessageSource();
  const { setCancelDialogActive } = useContext(CancelApplicationContext);
  const annualFlightSpendingOptions = useAnnualFlightSpendingOptions();
  const { language } = useContext(LanguageContext);
  const { selectedProduct } = useContext(ProductContext);

  const hasPartnerPlusBenefits = selectedProduct?.hasFeature(
    "PARTNER_PLUS_BENEFIT",
  );

  const hasMembershipReward = selectedProduct?.hasFeature("MEMBERSHIP_REWARDS");

  const titleKey = hasPartnerPlusBenefits
    ? "AdditionalBenefits.title-partner-plus-specific"
    : "AdditionalBenefits.title";

  return (
    <>
      <FlowHeader
        title={getMessage("FlowHeader.basicAccountApplication")}
        image={<ProductImage product={selectedProduct?.productRef} />}
        subTitle={context?.companyName}
      />
      <ApplicationPageContent>
        <ContentHeader title={getMessage(titleKey)} />
        <Formik
          key={language}
          enableReinitialize
          initialValues={initial}
          onSubmit={async (
            values: BasicAccountBenefitsModel,
            formikActions,
          ) => {
            await next(values);
            formikActions.setSubmitting(false);
          }}
          validationSchema={useAdditionalBenefitsValidationSchema()}
        >
          {({ values, handleSubmit, isSubmitting, submitCount, errors }) => {
            return (
              <Form>
                <FormContainer hasAdditionalTopMargin={true}>
                  {hasPartnerPlusBenefits && (
                    <PartnerPlusBenefits
                      hasLoyalties={values.hasLoyalties}
                      plusPartnerUsername={values.plusPartnerUsername}
                      isContactPersonSame={values.isContactPersonSame}
                      adminContactPerson={values.adminContactPerson}
                      partnerPlusBenefitGeneralDto={
                        values.partnerPlusBenefitGeneralDto
                      }
                      annualFlightSpendingOptions={annualFlightSpendingOptions}
                      contactPerson={values.contactPerson}
                    />
                  )}
                  <InsurancePlus />
                  {hasMembershipReward && <MembershipReward />}
                  <TurnoverBonus />
                  {selectedProduct?.hasFeature("PICTURE_OR_LOGO_ON_CARD") && (
                    <CustomizationCardOptions />
                  )}
                  <CashOnCard />
                </FormContainer>
                <ContentFooter>
                  <InvalidFormError
                    isDisplayed={isSubmitErrorMessageShown(submitCount, errors)}
                  />
                  <NavigationBar
                    isSubmitting={isSubmitting}
                    onNext={handleSubmit}
                    onCancel={() => setCancelDialogActive(true)}
                    cancelButtonText={getMessage("Onboarding.cancel")}
                    nextButtonText={getMessage("Onboarding.continue")}
                    onBack={() => previous()}
                    backButtonText={getMessage("Onboarding.back")}
                  />
                </ContentFooter>
              </Form>
            );
          }}
        </Formik>
      </ApplicationPageContent>
    </>
  );
}
